// AGC: Admin Panel - Threshold Editor Component
// AGC: Editable threshold levels with color-coded display

import React from 'react'
import type { HealthThreshold } from '../../../types/admin/models'

interface ThresholdEditorProps {
  thresholds: HealthThreshold[]
  onChange: (index: number, field: 'min' | 'label', value: number | string) => void
}

/**
 * AGC: ThresholdEditor - Edit health score thresholds
 * AGC: Color-coded cards for visual threshold configuration
 */
export const ThresholdEditor: React.FC<ThresholdEditorProps> = ({
  thresholds,
  onChange,
}) => (
  <div className="bg-slate-50 dark:bg-white/[0.02] rounded-xl p-5 border border-slate-200 dark:border-white/5">
    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
      Umbrais de Score (Thresholds)
    </h4>
    
    <div className="grid grid-cols-4 gap-3">
      {thresholds.map((t, i) => (
        <div
          key={i}
          className="p-4 rounded-lg"
          style={{
            backgroundColor: `${t.color}15`,
            border: `2px solid ${t.color}`,
          }}
        >
          <div
            className="text-xs uppercase tracking-wide font-semibold mb-2"
            style={{ color: t.color }}
          >
            {t.label}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">≥</span>
            <input
              type="number"
              value={t.min}
              min={0}
              max={100}
              onChange={(e) => onChange(i, 'min', Number(e.target.value))}
              className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-green-500 bg-white dark:bg-admin-card-dark"
            />
          </div>
        </div>
      ))}
    </div>

    {/* AGC: Visual threshold scale */}
    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-white/5">
      <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">Escala visual:</div>
      <div className="h-4 rounded-full flex overflow-hidden">
        {thresholds.map((t, i) => {
          // AGC: Calculate width based on threshold ranges
          const nextMin = i > 0 ? thresholds[i - 1].min : 100
          const width = nextMin - t.min
          return (
            <div
              key={i}
              className="h-full"
              style={{
                width: `${width}%`,
                backgroundColor: t.color,
              }}
            />
          )
        }).reverse()}
      </div>
      <div className="flex justify-between mt-1 text-xs text-slate-400 dark:text-slate-500">
        <span>0</span>
        <span>25</span>
        <span>50</span>
        <span>75</span>
        <span>100</span>
      </div>
    </div>
  </div>
)

export default ThresholdEditor


